// Settings → Channels: custom domain manager.
//
// List the tenant's domains with status pills + verification record details.
// Add via modal that shows the CNAME + ACME records the operator must add at
// their registrar. Refresh polls Cloudflare for status updates.

import { useEffect, useState, type FormEvent, type ReactElement } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { orpc } from '../lib/orpc.js';
import { friendly_error } from '../lib/friendly-error.js';

interface CustomDomain {
  id: string;
  hostname: string;
  status: 'pending' | 'verifying' | 'active' | 'failed' | 'removed';
  cf_hostname_status: string;
  cf_ssl_status: string;
  cname_target: string;
  verification: { type: string; name: string; value: string } | null;
  verification_errors: string[];
  // oRPC's wire encoding sends dates as ISO strings, but the inferred client
  // type matches the server's `z.date()` — TypeScript treats these as Date.
  verified_at: Date | null;
  removed_at: Date | null;
  created_at: Date;
  updated_at: Date;
}

export function SettingsChannelsPage(): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [show_add, set_show_add] = useState(false);

  const q = useQuery({
    queryKey: ['domains'],
    queryFn: () => orpc.domains.list({}),
  });

  const refresh_m = useMutation({
    mutationFn: (domain_id: string) => orpc.domains.refresh_status({ domain_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['domains'] }),
  });

  const remove_m = useMutation({
    mutationFn: (domain_id: string) => orpc.domains.remove({ domain_id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['domains'] }),
  });

  const domains = (q.data?.domains ?? []) as CustomDomain[];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-medium">{t('settings.channels.title', 'Channels')}</h1>
          <p className="text-xs text-ink/60 mt-1">
            {t(
              'settings.channels.subtitle',
              'Configure custom domains, WhatsApp numbers, and payment channels.',
            )}
          </p>
        </div>
      </div>

      <section className="bg-surface border border-line rounded-lg p-5 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-sm font-medium">
              {t('settings.channels.custom_domains', 'Custom domains')}
            </h2>
            <p className="text-[11px] text-ink/60 mt-0.5">
              {t(
                'settings.channels.custom_domains_hint',
                'Point your own domain (e.g. rameshchits.in) at the platform. TLS is provisioned automatically.',
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => set_show_add(true)}
            className="bg-primary hover:bg-primary-2 text-white px-3 py-1.5 rounded text-sm font-medium"
          >
            {t('settings.channels.add_domain', '+ Add domain')}
          </button>
        </div>

        {q.isLoading && <div className="text-xs text-ink/60">Loading…</div>}
        {q.error && <div className="text-xs text-bad">{friendly_error(q.error)}</div>}
        {!q.isLoading && domains.length === 0 && (
          <div className="text-center py-6 text-xs text-ink/60">
            {t('settings.channels.no_domains', 'No custom domains yet.')}
          </div>
        )}
        {domains.length > 0 && (
          <div className="divide-y divide-line/40 border border-line rounded">
            {domains.map((d) => (
              <DomainRow
                key={d.id}
                domain={d}
                refreshing={refresh_m.isPending}
                removing={remove_m.isPending}
                on_refresh={() => refresh_m.mutate(d.id)}
                on_remove={() => remove_m.mutate(d.id)}
              />
            ))}
          </div>
        )}
      </section>

      <WhatsAppNumberSection />

      {show_add && <AddDomainModal on_close={() => set_show_add(false)} />}
    </div>
  );
}

// Settings → Channels → WhatsApp number. Lets the operator send WhatsApp from
// their OWN number via OpenWA (free, enables per-chit groups) instead of the
// official Plivo path. Connect = scan a QR with a DEDICATED business number.
function WhatsAppNumberSection(): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [connecting, set_connecting] = useState(false);

  const status_q = useQuery({
    queryKey: ['wa-provider'],
    queryFn: () => orpc.whatsapp.provider_get({}),
  });

  const start_m = useMutation({
    mutationFn: () => orpc.whatsapp.connect_start({}),
    onSuccess: () => set_connecting(true),
  });

  const qr_q = useQuery({
    queryKey: ['wa-qr'],
    queryFn: () => orpc.whatsapp.connect_qr({}),
    enabled: connecting,
    refetchInterval: 4000,
  });

  const conn_q = useQuery({
    queryKey: ['wa-connect-status'],
    queryFn: () => orpc.whatsapp.connect_status({}),
    enabled: connecting,
    refetchInterval: 4000,
  });

  useEffect(() => {
    if (conn_q.data?.connected) {
      set_connecting(false);
      void qc.invalidateQueries({ queryKey: ['wa-provider'] });
    }
  }, [conn_q.data?.connected, qc]);

  const switch_m = useMutation({
    mutationFn: (provider: 'plivo' | 'openwa') => orpc.whatsapp.set_provider({ provider }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wa-provider'] }),
  });

  const provider = status_q.data?.provider ?? 'plivo';
  const connected = status_q.data?.connected ?? false;
  const available = status_q.data?.available ?? false;
  const qr = qr_q.data?.qr ?? null;
  const is_dry = typeof qr === 'string' && qr.startsWith('dryrun_qr');

  return (
    <section className="bg-surface border border-line rounded-lg p-5 mb-6">
      <div className="mb-4">
        <h2 className="text-sm font-medium">{t('settings.wa.title', 'WhatsApp number')}</h2>
        <p className="text-[11px] text-ink/60 mt-0.5">
          {t(
            'settings.wa.subtitle',
            'Send WhatsApp from the official gateway, or from your own number to also create a group per chit.',
          )}
        </p>
      </div>

      {provider === 'openwa' && connected ? (
        <div className="flex items-center justify-between gap-3 border border-good/30 bg-good-soft rounded p-3">
          <div className="text-xs text-good">
            ✓ {t('settings.wa.connected', 'Connected — messages send from your own number.')}
          </div>
          <button
            type="button"
            onClick={() => switch_m.mutate('plivo')}
            disabled={switch_m.isPending}
            className="px-2 py-1 text-xs border border-line rounded hover:bg-bg-elev disabled:opacity-50"
          >
            {t('settings.wa.switch_official', 'Switch to official')}
          </button>
        </div>
      ) : connecting ? (
        <div className="flex flex-col items-center gap-3 py-2">
          <p className="text-xs text-ink/70 text-center max-w-sm">
            {t(
              'settings.wa.scan_hint',
              'On the dedicated business phone: WhatsApp → Linked devices → Link a device, then scan this code.',
            )}
          </p>
          {is_dry ? (
            <div className="w-44 h-44 grid place-items-center border border-dashed border-line rounded text-[11px] text-ink/50 text-center px-3">
              {t('settings.wa.qr_dev', 'Dev mode — no live gateway, so no real QR.')}
            </div>
          ) : qr ? (
            <img
              src={qr.startsWith('data:') ? qr : `data:image/png;base64,${qr}`}
              alt="WhatsApp QR"
              className="w-44 h-44 border border-line rounded"
            />
          ) : (
            <div className="w-44 h-44 grid place-items-center text-[11px] text-ink/50">
              {t('settings.wa.qr_loading', 'Generating QR…')}
            </div>
          )}
          <div className="text-[11px] text-ink/55">
            {t('settings.wa.waiting', 'Waiting for scan…')} ({conn_q.data?.status ?? '…'})
          </div>
          <button
            type="button"
            onClick={() => set_connecting(false)}
            className="px-2 py-1 text-[11px] text-ink/60 underline"
          >
            {t('actions.cancel', 'Cancel')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="border border-warn/30 bg-warn-soft rounded p-3 text-[11px] text-warn space-y-1">
            <div className="font-medium">⚠ {t('settings.wa.warn_title', 'Use a dedicated number')}</div>
            <div>
              {t(
                'settings.wa.warn_body',
                'WhatsApp may ban or unlink an automated number. Connect a SEPARATE business SIM — not your personal WhatsApp — so a ban never affects your real account.',
              )}
            </div>
          </div>
          {!available && !status_q.isLoading && (
            <div className="border border-line bg-bg-elev rounded p-3 text-[11px] text-ink/70">
              {t(
                'settings.wa.unavailable',
                'Own-number WhatsApp isn’t enabled on this server yet — the OpenWA gateway has to be running first. Contact your administrator to turn it on.',
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => start_m.mutate()}
            disabled={start_m.isPending || !available}
            title={
              available
                ? undefined
                : t('settings.wa.unavailable_tip', 'OpenWA gateway not set up on the server yet')
            }
            className="bg-primary hover:bg-primary-2 text-white px-4 py-2 rounded text-sm font-medium disabled:bg-ink/20 disabled:cursor-not-allowed"
          >
            {start_m.isPending
              ? '…'
              : t('settings.wa.connect', 'Connect my number (enables groups)')}
          </button>
          {start_m.error && (
            <div className="text-[11px] text-bad">
              {t(
                'settings.wa.connect_failed',
                'Couldn’t reach the WhatsApp gateway. It may not be set up on the server yet.',
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function DomainRow({
  domain,
  refreshing,
  removing,
  on_refresh,
  on_remove,
}: {
  domain: CustomDomain;
  refreshing: boolean;
  removing: boolean;
  on_refresh: () => void;
  on_remove: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const status_cls =
    domain.status === 'active'
      ? 'bg-good-soft text-good'
      : domain.status === 'failed'
        ? 'bg-bad-soft text-bad'
        : 'bg-warn-soft text-warn';
  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="font-mono text-sm">{domain.hostname}</div>
          <div className="text-[11px] text-ink/50 mt-0.5">
            cf hostname: {domain.cf_hostname_status} · ssl: {domain.cf_ssl_status}
          </div>
        </div>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${status_cls}`}>
          {domain.status}
        </span>
      </div>

      {domain.status !== 'active' && (
        <div className="bg-bg-elev rounded p-3 text-[11px] font-mono mb-2 space-y-1">
          <div className="text-ink/60 uppercase tracking-wider text-[10px]">
            {t('settings.channels.records_to_add', 'Add at your registrar')}:
          </div>
          <div>
            <span className="text-ink/60">CNAME </span>
            {domain.hostname} → {domain.cname_target}
          </div>
          {domain.verification && (
            <div>
              <span className="text-ink/60">{domain.verification.type.toUpperCase()} </span>
              {domain.verification.name} → {domain.verification.value}
            </div>
          )}
          {domain.verification_errors.length > 0 && (
            <div className="text-bad mt-1">
              {domain.verification_errors.map((e, i) => (
                <div key={i}>⚠ {e}</div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between text-[11px] text-ink/60">
        <span>
          {domain.verified_at
            ? `Verified ${new Date(domain.verified_at).toLocaleString()}`
            : `Created ${new Date(domain.created_at).toLocaleString()}`}
        </span>
        <div className="flex gap-1">
          {domain.status !== 'active' && (
            <button
              type="button"
              onClick={on_refresh}
              disabled={refreshing}
              className="px-2 py-1 text-xs border border-line rounded hover:bg-bg-elev disabled:opacity-50"
            >
              {refreshing ? '…' : t('actions.refresh', 'Refresh')}
            </button>
          )}
          <button
            type="button"
            onClick={on_remove}
            disabled={removing}
            className="px-2 py-1 text-xs border border-bad/30 text-bad rounded hover:bg-bad-soft disabled:opacity-50"
          >
            {removing ? '…' : t('actions.remove', 'Remove')}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddDomainModal({ on_close }: { on_close: () => void }): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [hostname, set_hostname] = useState('');
  const m = useMutation({
    mutationFn: () => orpc.domains.add({ hostname: hostname.trim().toLowerCase() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['domains'] });
      on_close();
    },
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (!hostname.trim()) return;
    m.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm">
      <form
        onSubmit={submit}
        className="bg-surface border border-line rounded-lg w-full max-w-md p-6 shadow-lg"
      >
        <h2 className="text-lg font-medium mb-1">
          {t('settings.channels.add_domain_title', 'Add custom domain')}
        </h2>
        <p className="text-xs text-ink/60 mb-4">
          {t(
            'settings.channels.add_domain_hint',
            'Once added, we will give you CNAME + verification records to set at your registrar. TLS is automatic.',
          )}
        </p>
        <label className="block mb-3">
          <span className="block text-sm mb-1">
            {t('settings.channels.hostname', 'Hostname')}
          </span>
          <input
            type="text"
            value={hostname}
            onChange={(e) => set_hostname(e.target.value)}
            placeholder="rameshchits.in"
            className="w-full px-3 py-2 border border-line rounded text-sm font-mono"
            autoFocus
            required
          />
        </label>
        {m.error && (
          <div className="px-3 py-2 bg-bad-soft text-bad text-xs rounded mb-3">
            {friendly_error(m.error)}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={on_close}
            className="px-4 py-2 rounded border border-line text-ink/70 hover:bg-bg-elev text-sm"
          >
            {t('actions.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={m.isPending}
            className="bg-primary hover:bg-primary-2 disabled:bg-ink/20 text-white px-4 py-2 rounded text-sm font-medium"
          >
            {m.isPending ? '…' : t('actions.add', 'Add')}
          </button>
        </div>
      </form>
    </div>
  );
}
